if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

// Brands/Designers/Showrooms aren't native predictive search resources, and
// Liquid can only iterate 250 metaobjects per page, so the full lists are
// loaded once per page load from the Storefront API and matched client-side.
window.PredictiveSearchEntities = window.PredictiveSearchEntities || {
  lists: null,

  // Rendered in this order, each slotted between Collections and Articles.
  types: ['brands', 'designers', 'showrooms'],

  load(config) {
    if (!this.lists) {
      this.lists = Promise.all(this.types.map((type) => this.fetchEntries(config, type)))
        .then((results) => Object.fromEntries(this.types.map((type, index) => [type, results[index]])))
        .catch((error) => {
          this.lists = null;
          throw error;
        });
    }
    return this.lists;
  },

  async fetchEntries(config, type) {
    // Brands/designers: "name" is a reference to a brands_list / designers_list
    // entry whose own "name" field holds the text; display_name is the fallback.
    // Showrooms have no "name" field, only display_name, plus location fields
    // that are searchable. Keys a type doesn't have just come back null.
    const query = `query($cursor: String) {
      metaobjects(type: "${type}", first: 250, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes {
          onlineStoreUrl
          name: field(key: "name") { reference { ... on Metaobject { field(key: "name") { value } } } }
          displayName: field(key: "display_name") { value }
          city: field(key: "city") { value }
          state: field(key: "state_province") { value }
          country: field(key: "country") { value }
          isMonospace: field(key: "is_monospace") { value }
        }
      }
    }`;

    const entries = [];
    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storefrontToken
        },
        body: JSON.stringify({ query, variables: { cursor } })
      });
      const json = await response.json();
      const page = json.data.metaobjects;

      page.nodes.forEach((node) => {
        const name = node.name?.reference?.field?.value || node.displayName?.value;
        if (!name || entries.some((entry) => entry.name === name)) return;

        // Path only, so the link stays on whatever domain/locale the visitor is on.
        let url = null;
        if (node.onlineStoreUrl) {
          try {
            url = new URL(node.onlineStoreUrl).pathname;
          } catch (error) {
            url = null;
          }
        }

        // Location only applies to showrooms; brands also have a "country"
        // field, but brands/designers should match on name alone.
        let location = '';
        let searchText = name.toLowerCase();
        let isMonobrand = false;
        let isOutsideUS = false;
        if (type === 'showrooms') {
          const city = node.city?.value || '';
          const state = node.state?.value || '';
          const country = node.country?.value || '';
          location = [city, state || country].filter(Boolean).join(', ');
          isMonobrand = (node.isMonospace?.value || '').trim().toLowerCase() === 'yes';
          // Keywords included so queries like "miami showroom" or
          // "monobrand chicago" match; only monobrand entries get the latter.
          const keywords = isMonobrand ? 'showrooms monobrand mono-brand' : 'showrooms';
          searchText = [name, city, state, country, keywords].join(' ').toLowerCase();
          isOutsideUS = country.trim().toLowerCase() !== 'united states';
        }

        entries.push({ name, url, location, searchText, isMonobrand, isOutsideUS });
      });

      hasNextPage = page.pageInfo.hasNextPage;
      cursor = page.pageInfo.endCursor;
    }

    // Showrooms: United States first, then the rest of the world; within each,
    // regular showrooms before monobrand spaces (is_monospace = Yes); then
    // alphabetical. Both flags are always false for brands/designers.
    return entries.sort((a, b) =>
      (a.isOutsideUS - b.isOutsideUS) ||
      (a.isMonobrand - b.isMonobrand) ||
      a.name.localeCompare(b.name)
    );
  },

  async render(container) {
    const slot = container.querySelector('[data-predictive-entities]');
    if (!slot) return;

    const term = (slot.dataset.terms || '').trim().toLowerCase();
    if (!term) return;

    let lists;
    try {
      lists = await this.load(slot.dataset);
    } catch (error) {
      console.error('Predictive search: could not load brands/designers/showrooms', error);
      return;
    }

    // A newer response may have replaced this one while the lists loaded.
    if (!slot.isConnected) return;

    const limit = parseInt(slot.dataset.limit, 10) || 3;
    const html = this.types
      .map((type) => this.buildGroup(lists[type], term, limit, slot.dataset[`${type}Heading`], slot.dataset[`${type}Url`]))
      .join('');

    if (!html) return;

    slot.insertAdjacentHTML('beforebegin', html);
    container.querySelector('[data-predictive-empty]')?.remove();
  },

  buildGroup(entries, term, limit, heading, fallbackUrl) {
    // Every word must appear somewhere, in any order ("showroom miami" works too).
    const words = term.split(/\s+/).filter(Boolean);
    const matches = entries.filter((entry) => words.every((word) => entry.searchText.includes(word))).slice(0, limit);
    if (matches.length === 0) return '';

    const items = matches
      .map((entry) => {
        const location = entry.location
          ? `<span class="block text-sm leading-tight" style="color: rgb(var(--color-foreground) / 0.6); margin-top: 0.25rem;">${this.escape(entry.location)}</span>`
          : '';
        return `<li><a class="reversed-link text-base md:text-lg leading-tight font-medium" href="${this.escape(entry.url || fallbackUrl)}">${this.escape(entry.name)}</a>${location}</li>`;
      })
      .join('');

    return `<div class="grid gap-3 md:gap-4 w-full">
      <p class="search__heading block text-xs uppercase leading-tight tracking-widest">${this.escape(heading)}</p>
      <ul class="grid gap-2">${items}</ul>
    </div>`;
  },

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[char]);
  }
};

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          const url = this.buildUrl().toString();
          this.renderSection(url, event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
      }

      getQuery() {
        return this.input.value.trim();
      }

      buildUrl() {
        const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url)
          : this.renderSectionFromFetch(url);
      }

      renderSectionFromCache(url) {
        const responseText = this.cachedMap.get(url);
        this.renderSearchResults(responseText);

        this.setAttribute('results', '');
      }

      renderSectionFromFetch(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        
        this.setAttribute('loading', '');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.renderSearchResults(responseText);
            this.cachedMap.set(url, responseText);

            this.removeAttribute('loading');
            this.setAttribute('results', '');
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            }
            else {
              console.error(error);
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const container = document.getElementById(id);
        if (container === null) return;

        container.innerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id).innerHTML;

        window.PredictiveSearchEntities.render(container);
      }
    }, { extends: 'form' }
  );
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
